import { LinkProfile, Pagination } from '@ekkogmbh/apisdk';
import { Add, Delete, Edit } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from 'src/Common/Components/GenericDialog';
import { ColumnDef, DataTable, dataTableHighlight } from 'src/Common/Components/DataTable';
import Permissions from 'src/Common/Permissions';
import { LinkProfilePanel } from './LinkProfilePanel';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { Chip, Stack, Typography } from '@mui/material';
import ObjectField from 'src/Common/Components/DataTable/ObjectField';
import ScrollableStack from 'src/Common/Components/ScrollableStack';
import CompartmentSelectorChip from 'src/CompartmentSelectorManagement/Components/CompartmentSelectorChip';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useNavigation from 'src/Common/Providers/NavigationProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';

// Coordinates minHeight across all scrollable columns so scroll
// indicator chevrons sit at the same vertical position in each row.
const ROW_MIN_HEIGHT = 64;

export const LinkProfileManagementContent = (): React.JSX.Element => {
  const { api } = useEkko();
  const { scrollTop } = useNavigation();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();

  const [isProcessing, startProcess] = useRequest<unknown>();
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false);
  const [selectedProfile, setSelectedProfile] = useState<LinkProfile>();

  const dismiss = useCallback(() => {
    setSelectedProfile(undefined);
    setPanelOpen(false);
    setDialogOpen(false);
  }, []);

  const deleteProfile = useCallback(() => {
    startProcess(
      () => api.deleteLinkProfile(selectedProfile!.coordinate, selectedProfile!.name),
      () => {
        dismiss();
        scrollTop();
        refresh();
      },
    );
  }, [selectedProfile, refresh]);

  const fetchProfiles = useCallback((pagination: Pagination) => api.getLinkProfiles(pagination), []);

  const panel = useMemo(() => <LinkProfilePanel profile={selectedProfile} closeHandler={dismiss} />, [selectedProfile]);

  const columns: ColumnDef[] = useMemo(
    () => [
      {
        header: 'Name',
        body: (entry: LinkProfile, searchText?: string) => (
          <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
            {searchText ? dataTableHighlight(entry.name, [searchText]) : entry.name}
          </ScrollableStack>
        ),
        filterField: 'name',
        sortField: 'LP.name',
        proportionalContent: '25%',
      },
      {
        header: 'Coordinate',
        body: (entry: LinkProfile, searchText?: string) => (
          <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT} alignItems="center">
            {searchText ? dataTableHighlight(entry.coordinate, [searchText]) : entry.coordinate}
          </ScrollableStack>
        ),
        filterField: 'coordinate',
        sortField: 'N.materializedPath',
        proportionalContent: '25%',
      },
      {
        header: 'Technology',
        body: (entry: LinkProfile) => entry.technology,
        fitContent: true,
      },
      {
        header: 'Template-Groups',
        body: (entry: LinkProfile) => {
          const templateGroups = Object.keys(entry.templateGroups);

          return (
            <>
              {templateGroups.map((name: string) => {
                const isAutoGroup = entry.autoTemplateGroup === name;
                return (
                  <Chip
                    style={{ margin: 8 }}
                    key={`group-field-${name}`}
                    variant={isAutoGroup ? 'outlined' : undefined}
                    label={
                      <Typography variant={'subtitle2'} color={'textPrimary'}>
                        {name}
                      </Typography>
                    }
                  />
                );
              })}
            </>
          );
        },
        fitContent: true,
      },
      {
        header: 'Compartment-Selector',
        body: (entry: LinkProfile, searchText?: string) => (
          <CompartmentSelectorChip compartmentSelector={entry.compartmentSelector} highlightText={searchText} />
        ),
        fitContent: true,
      },
      {
        header: 'Input-Instructions',
        body: (entry: LinkProfile) => (
          <ScrollableStack direction="row" width="100%" minHeight={ROW_MIN_HEIGHT}>
            <Stack direction="column" sx={{ flexShrink: 0 }}>
              {[
                { label: 'Label Input', value: entry.labelInputInstructions ?? '-' },
                { label: 'Compartment Input', value: entry.compartmentInputInstructions ?? '-' },
              ].map(({ label, value }) => (
                <Stack key={label} direction="row" spacing={1} style={{ margin: 8 }}>
                  <Typography color="textSecondary" fontWeight={700} whiteSpace="nowrap">
                    {label}:
                  </Typography>
                  <Typography color="textPrimary" whiteSpace="nowrap">
                    {value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </ScrollableStack>
        ),
        proportionalContent: '50%',
      },
      {
        header: 'Compartment',
        body: (entry: LinkProfile) => (
          <ObjectField
            direction={'column'}
            fields={[
              {
                key: 'Creation',
                value: !!(entry.noMatchStrategy && entry.noMatchStrategy.name !== 'null'),
              },
              {
                key: 'Removal',
                value: !!entry.removeObsoleteCompartments,
              },
            ]}
          />
        ),
        fitContent: true,
      },
    ],
    [],
  );

  return (
    <ContentControl
      panel={panel}
      dialog={
        <GenericDialog
          type="confirmation"
          maxWidth={'sm'}
          fullWidth={true}
          centered={true}
          open={isDialogOpen}
          title={'Delete Link Profile'}
          text={selectedProfile?.name ?? ''}
          onClose={dismiss}
          onConfirm={deleteProfile}
        />
      }
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          tooltip={'Add Link Profile'}
          requiredPermission={Permissions.LINK_PROFILES_WRITE}
          onClick={() => {
            dismiss();
            setSelectedProfile(undefined);
            setPanelOpen(true);
            scrollTop();
          }}
        />,
      ]}
    >
      <DataTable
        actions={[
          {
            title: 'Edit',
            icon: <Edit />,
            disabled: isProcessing,
            onClick: (entry: LinkProfile) => {
              setSelectedProfile(entry);
              setPanelOpen(true);
            },
          },
          {
            title: 'Delete',
            icon: <Delete />,
            disabled: isProcessing,
            onClick: (entry: LinkProfile) => {
              setSelectedProfile(entry);
              setDialogOpen(true);
            },
          },
        ]}
        columns={columns}
        fetchItems={fetchProfiles}
      />
    </ContentControl>
  );
};
